import * as types from 'store/Manage/types';
import * as FileSaver from 'file-saver';
import api from 'services/api';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import set from 'lodash/set';
import { MOCK_VULN_ID } from 'Common/Components/Tour/helpers/mockVuln';
import { setTourFinished, setNewTourFinished } from 'store/Preferences/Tour/actions';
import {
  selectVulnsList,
  selectVulnDetail,
  selectUsesMockVulns,
  selectVulnsCount,
  selectLastSelected,
  selectVulnsSelected,
  selectShowDetail,
  selectModalBulkUpdateField,
  selectModalBulkUpdateValue,
  selectContextMenuXPos,
  selectContextMenuYPos,
  selectSelectAll,
  selectBulkUpdateCustomAttribute
} from 'store/Manage/selectors';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { getData } from 'Screens/Contextualization/Workspaces/actions/Actions';
import {
  selectGroupByField, selectIsGrouping, selectRowsPerPage,
  selectQueryParam, selectAdvancedFilterQueryParam
} from 'store/Filters/selectors';
import { redirect } from 'store/Router/actions';
import { MODAL_MANAGE_BULK_UPDATE, MODAL_MANAGE_BULK_UPDATE_CONFIRMATION } from 'store/modals/modals';
import { closeModal, openModal } from 'store/modals/actions';
import { setFilterError } from 'store/Filters/actions';
import { newGetVulns, setVulnsFilterError } from './filterActions';
import { getVulnsCountInWs } from 'Screens/Contextualization/Faraday/actions/Actions';
import { VULN_ASSET_ERROR } from 'store/Contextualization/AssetDetail/types';
import { selectManageFields } from 'store/Preferences/manage/contextSelectors';
import VALID_COLUMNS from 'Screens/Contextualization/Manage/components/CustomActionBar/components/ExportCSV/constants';
import { getVulnId, sameVulnId } from 'Common/Functions/vulnerabilityId';
import { getExportCsvColumns } from './exportCsv';

export function setImagePreview (imagePreview) {
  return (dispatch, getState) => {
    dispatch({
      type: types.SET_IMAGE_PREVIEW, imagePreview
    });
  };
}

const addDeleteController = (vulns, vulnList) => { // if all vulns are in vulnList then unselect then if not
  const allVulnsAreSelected = vulns.every((testVuln) => vulnList.some((vuln) => sameVulnId(vuln, testVuln)));

  if (allVulnsAreSelected) return [types.VULN_UNSELECTED, vulns];
  return [types.VULN_SELECTED, vulns];
};

const selectCalculator = (e, vuln, vulnList, areVulnsSelected) => (dispatch, getState) => {
  const pivot = selectLastSelected(getState());
  const index = vulnList.findIndex((el) => sameVulnId(el, vuln));
  const vulnsSelected = selectVulnsSelected(getState());

  dispatch({ type: types.NEW_PIVOT, payload: index });
  if (e.shiftKey && pivot !== -1 && areVulnsSelected) { // if already had vuln selected and shift key is pressed
    const start = Math.min(pivot, index);
    const end = Math.max(pivot, index) + 1;
    const vulns = vulnList.slice(start, end);
    const [type, payload] = addDeleteController(vulns, vulnsSelected);
    return dispatch({ type, payload });
  }
  const [type, payload] = addDeleteController([vuln], vulnsSelected);
  return dispatch({ type, payload });
};

export const selectRow = (e, vuln) => (dispatch, getState) => {
  const allVulns = selectVulnsList(getState());
  const isGroupingBy = selectIsGrouping('vulns', getState());
  const vulnsSelected = selectVulnsSelected(getState());

  if (isGroupingBy) {
    const group = allVulns.find((g) => g.groupData && g.groupData.find((el) => sameVulnId(el, vuln)));
    if (!group) return; // si no hay groupData no se puede hacer calculos con las vulns
    const vulnGroup = group.groupData;
    const areVulnsSelected = vulnsSelected.length > 0 && vulnGroup.some((el) => sameVulnId(el, vuln));
    dispatch(selectCalculator(e, vuln, vulnGroup, areVulnsSelected, vulnsSelected));
  } else {
    dispatch(selectCalculator(e, vuln, allVulns, vulnsSelected.length > 0));
  }
};

export const unSelectAll = () => (dispatch) => dispatch({ type: types.UNSELECT_ALL_VULN });

export const selectAllVulns = () => (dispatch, getState) => {
  const vulnsList = selectVulnsList(getState());
  const vulnsSelected = selectVulnsSelected(getState());
  const vulnCount = selectVulnsCount(getState());
  const pageSize = selectRowsPerPage('vulns', getState());

  if (vulnsSelected.length === vulnCount || vulnsSelected.length >= pageSize) return dispatch(unSelectAll());

  return dispatch({ type: types.SELECT_ALL_VULN, vulnsList });
};

export const selectTotalVulns = () => (dispatch, getState) => { // includes server vulns
  return dispatch({ type: types.SELECT_TOTAL_VULNS });
};

export const unselectTotalVulns = () => (dispatch, getState) => { // includes server vulns
  return dispatch({ type: types.UNSELECT_TOTAL_VULNS });
};

export const selectConfirmedVulns = () => (dispatch, getState) => {
  const state = getState();
  const vulnsList = selectVulnsList(state).filter((vuln) => (vuln.confirmed));

  return dispatch({ type: types.SELECT_ALL_VULN, vulnsList });
};

export const selectNotConfirmedVulns = () => (dispatch, getState) => {
  const state = getState();
  const vulnsList = selectVulnsList(state).filter((vuln) => (!vuln.confirmed));
  return dispatch({ type: types.SELECT_ALL_VULN, vulnsList });
};

// Summary: Show confirmation modal when user delete vuln/s
export function showVulnModalDelete () {
  return (dispatch) => {
    dispatch({ type: types.SHOW_MODAL_DELETE_CONFIRMATION_VULNS });
  };
}

// Summary: Hide confirmation modal when user delete vuln/s
export function hideVulnModalDelete () {
  return (dispatch) => {
    dispatch({ type: types.HIDE_MODAL_DELETE_CONFIRMATION_VULNS });
  };
}

// Summary: reset state of manage screen
export function resetState () {
  return (dispatch) => {
    dispatch({ type: types.RESET_STATE_MANAGE });
  };
}

function exportCsv (result) {
  return async (_, getState) => {
    const workspaceSelected = selectCurrentWorkspace(getState());
    let title = '';

    if (workspaceSelected) title = `SR-${workspaceSelected}`;
    else title = 'Vulnerability Model CSV';

    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.csv';

    const data = new Blob([result], { type: fileType });
    FileSaver.saveAs(data, title + fileExtension);
  };
}

export function resetErrorValue () {
  return (dispatch) => {
    dispatch({ type: types.RESET_ERROR_VALUE });
  };
}

export function showManageLeftFilters (visible, breakpoint) {
  return (dispatch) => {
    dispatch({ type: types.SHOW_MANAGE_LEFT_FILTERS, visible, breakpoint });
  };
}

export function redirectToVulnDetail (id) {
  return (dispatch, getState) => {
    const currentWorkspace = selectCurrentWorkspace(getState());
    dispatch(redirect(`/manage/${currentWorkspace}/${id}`));
  };
}

export function redirectToManage () {
  return (dispatch, getState) => {
    const currentWorkspace = selectCurrentWorkspace(getState());
    dispatch(redirect(`/manage/${currentWorkspace}`));
  };
}

export function setVulnerabilityDetailTab (vulnDetailSelectedTab) {
  return (dispatch) => {
    dispatch({ type: types.SET_VULNERABILITY_DETAIL_TAB, vulnDetailSelectedTab });
  };
}

export function showVulnerabilityDetail (id, vulnDetailSelectedTab) {
  return (dispatch, getState) => {
    const currentWorkspace = selectCurrentWorkspace(getState());
    if (id > 0) {
      dispatch(redirect(`/manage/${currentWorkspace}/${id}`));
      // Redirecting to manage/id calls loadVulnerabilityDetail, fetching the detail and showing it.
      dispatch(setVulnerabilityDetailTab(vulnDetailSelectedTab));
    }
  };
}

export function loadVulnerabilityDetail (id) {
  return async (dispatch, getState) => {
    dispatch({ type: types.LOAD_VULNERABILITY_DETAIL_REQUEST });

    if (id > 0) {
      const currentWorkspace = selectCurrentWorkspace(getState());
      try {
        const vuln = await api.manage.fetchById(currentWorkspace, id);
        dispatch({ type: types.LOAD_VULNERABILITY_DETAIL_SUCCESS, vuln });
      } catch (e) {
        dispatch({ type: types.LOAD_VULNERABILITY_DETAIL_FAILURE, error: e });
        dispatch(redirectToManage());
      }
    } else {
      dispatch({ type: types.LOAD_VULNERABILITY_DETAIL_FAILURE, error: new Error('Invalid ID') });
    }
  };
}

export function hideVulnerabilityDetail () {
  return (dispatch, getState) => {
    const showVulnDetail = selectShowDetail(getState());
    if (showVulnDetail) dispatch({ type: types.HIDE_VULNERABILITY_DETAIL });
  };
}

export function addEvidences (evidences, getEvidences, vulnId) {
  return async (dispatch, getState) => {
    const responseSession = await api.faraday.getSession();
    const currentWorkspace = selectCurrentWorkspace(getState());
    const promises = evidences.map((evidence) => {
      const newEvidenceName = evidence.name.replace(/\s+/g, '');
      const renameEvidence = new File([evidence], newEvidenceName);
      const fd = new FormData();
      fd.append('csrf_token', responseSession.csrf_token);
      fd.append('file', renameEvidence);
      fd.append('description', '');
      return api.attachments.saveAttachments(currentWorkspace, vulnId, fd);
    });

    try {
      await Promise.all(promises);
    } catch (e) {
      const doc = e.message ? new DOMParser().parseFromString(e.message, 'text/html') : null;
      const message = e.message ? doc.documentElement.textContent : 'An error has occurred';
      dispatch({ type: types.UPDATE_VULN_MANAGE_PREVIEW_FAILURE, error: message });
    }
    getEvidences();
  };
}

export function addDescriptionInEvidence (attachmentsName, description, vulnDetailId) {
  return async (dispatch, getState) => {
    const currentWorkspace = selectCurrentWorkspace(getState());
    try {
      const data = { description };
      await api.attachments.addDescriptionInEvidence(currentWorkspace, vulnDetailId, data, attachmentsName);
    } catch (e) {
      const doc = e.message ? new DOMParser().parseFromString(e.message, 'text/html') : null;
      const message = e.message ? doc.documentElement.textContent : 'An error has occurred';
      dispatch({ type: types.UPDATE_VULN_MANAGE_PREVIEW_FAILURE, error: message });
    }
  };
}

function vulnPreviewEditSuccedCallback (vulnBefore, vulnAfter) {
  return (dispatch, getState) => {
    const groupBy = selectGroupByField('vulns', getState());
    const vulnsList = selectVulnsList(getState());

    if (groupBy) {
      // se actualiza la tabla agrupada
      const indexGroupDataAfter = vulnsList.findIndex((x) => x[groupBy] === vulnAfter[groupBy]);
      const groupDataAfter = vulnsList[indexGroupDataAfter].groupData || [];
      const indexVulnAfter = groupDataAfter.findIndex((x) => vulnAfter._id === x.id || vulnAfter._id === x._id);
      if (indexVulnAfter < 0) { // no lo encontro, lo borro
        const indexGroupDataBefore = vulnsList.findIndex((x) => x[groupBy] === vulnBefore[groupBy]);
        const groupDataBefore = vulnsList[indexGroupDataBefore].groupData || {};
        const indexVulnBefore = groupDataBefore.findIndex((x) => vulnAfter._id === x.id || vulnAfter._id === x._id);
        // lo quito del grupo.
        vulnsList[indexGroupDataBefore].groupData.splice(indexVulnBefore, 1);
        // eslint-disable-next-line no-negated-condition
        if (!vulnsList[indexGroupDataAfter].groupData) vulnsList[indexGroupDataAfter].count += 1;
        else vulnsList[indexGroupDataAfter].groupData.push(vulnAfter);
      } else {
        vulnsList[indexGroupDataAfter].groupData[indexVulnAfter] = vulnAfter;
      }
    } else {
      // se actualiza la tabla
      const index = vulnsList.findIndex((x) => vulnAfter._id === x.id || vulnAfter._id === x._id);
      vulnsList[index] = vulnAfter;
    }

    dispatch({ type: types.UPDATE_VULN_MANAGE_PREVIEW_SUCCESS, vuln: vulnAfter, vulnsList });
  };
}

function vulnPreviewEditErrorCallback (error) {
  return (dispatch) => {
    dispatch({ type: types.UPDATE_VULN_MANAGE_PREVIEW_FAILURE, error: error.message || 'An error has occurred' });
  };
}

export function updateVuln (vulnBefore, field, value) {
  return async (dispatch, getState) => {
    const workspaceSelected = selectCurrentWorkspace(getState());
    let vulnAfter = { ...vulnBefore };
    set(vulnAfter, field, value);

    try {
      let newRefs = [];
      if (field === 'refs') {
        newRefs = value.map((ref) => ({ name: ref, type: 'other' }));
        vulnAfter = await api.manage.updateVuln(workspaceSelected, { _id: vulnBefore._id, refs: newRefs });
      } else {
        vulnAfter = await api.manage.updateVuln(workspaceSelected, { _id: vulnBefore._id, [field]: value });
      }

      dispatch(vulnPreviewEditSuccedCallback(vulnBefore, vulnAfter));
      if (field === 'confirmed') dispatch(getVulnsCountInWs());
    } catch (error) {
      dispatch(vulnPreviewEditErrorCallback(error));
    }
  };
}

export const updateHostParent = (id, newParent, newParentType, ws = null) => async (dispatch, getState) => {
  if (!ws) ws = selectCurrentWorkspace(getState());
  const vulnsList = selectVulnsList(getState());

  const data = {
    parent: newParent,
    parent_type: newParentType || 'Host'
  };

  try {
    const vulnRes = await api.manage.updateVuln(ws, { _id: id, ...data });
    const index = vulnsList.findIndex((x) => vulnRes._id === x.id || vulnRes._id === x._id);
    vulnsList[index] = vulnRes;
    dispatch({ type: types.UPDATE_VULN_MANAGE_PREVIEW_SUCCESS, vuln: vulnRes, vulnsList });
  } catch (error) {
  }
};

export function selectGroup (index, selected) {
  return (dispatch) => {
    dispatch({ type: types.SELECT_GROUP_MANAGE, index, selected });
  };
}

export function selectVulnGroup (vuln, selected) {
  return (dispatch) => {
    dispatch({ type: types.SELECT_VULN_GROUP_MANAGE, vuln, selected });
  };
}

export function setBulkUpdateField (field) {
  return (dispatch) => {
    dispatch({ type: types.MANAGE_SET_BULK_UPDATE_FIELD, field });
  };
}

export function setBulkUpdateValue (value) {
  return (dispatch) => {
    dispatch({ type: types.MANAGE_SET_BULK_UPDATE_VALUE, value });
  };
}

export function addBulkUpdateValue (value) {
  return (dispatch) => {
    dispatch({ type: types.MANAGE_ADD_BULK_UPDATE_VALUE, value });
  };
}

export function removeBulkUpdateValue (value) {
  return (dispatch) => {
    dispatch({ type: types.MANAGE_REMOVE_BULK_UPDATE_VALUE, value });
  };
}

export function setCustomAttributeField (customAttribute) {
  return (dispatch) => {
    dispatch({ type: types.MANAGE_SET_BULK_CUSTOM_ATTRIBUTE, customAttribute });
  };
}

export function refreshVulnsList (vulnsList, vulnsSelected, vulnDetail) {
  return (dispatch, getState) => {
    dispatch({
      type: types.REFRESH_VULNS, vulnsList, vulnsSelected, vulnDetail, vulnsCount: selectVulnsCount(getState())
    });
  };
}

export const getImpactData = (value, selectedVulns) => {
  const keyNames = Object.keys(selectedVulns[0].impact);
  let impact = {};
  keyNames.forEach((imp) => {
    impact = {
      ...impact,
      [imp]: value.some((x) => x === imp)
    };
  });
  return { impact };
};

export function bulkUpdateVulns () {
  return async (dispatch, getState) => {
    const state = getState();
    const field = selectModalBulkUpdateField(state);

    dispatch({ type: types.CONFIRMATION_CHANGE_START });

    try {
      const vulnsList = selectVulnsList(state);
      const vulnsSelected = selectVulnsSelected(state);
      const vulnDetail = selectVulnDetail(state);
      const value = selectModalBulkUpdateValue(state);
      const workspaceSelected = selectCurrentWorkspace(state);
      const selectAll = selectSelectAll(state);
      const vulnIDs = vulnsSelected.map(getVulnId).filter((id) => id !== null && typeof id !== 'undefined');
      const customAttribute = selectBulkUpdateCustomAttribute(state);
      const customAttributeType = get(customAttribute, 'field_type', '');
      const customAttributeFieldName = get(customAttribute, 'field_name', '');
      const customAttributeValue = ((customAttributeType === 'choice') && (value === '(Empty)')) ? '' : value;

      const advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulns');
      const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
      const standardQueryParam = selectQueryParam('vulns', state);
      const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

      let data = {};

      if (field === 'references') data = { refs: value.map((v) => ({ name: v, type: 'other' })) };
      else if (field === 'policy violations') data = { policyviolations: value };
      else if (field === 'description') data = { description: value, desc: value };
      else if (field === 'impact') data = getImpactData(value, vulnsSelected);
      else if (field === 'custom_fields' && (vulnsSelected?.length > 1)) data = { custom_fields: { [customAttributeFieldName]: customAttributeValue } };
      else if (field === 'custom_fields' && (vulnsSelected?.length === 1)) data = { custom_fields: { ...vulnsSelected[0].custom_fields, [customAttributeFieldName]: customAttributeValue } };
      else data = { [field]: value };

      const isDetailVulnSelected = vulnsSelected.some((selectedVuln) => !isEmpty(vulnDetail) && sameVulnId(selectedVuln, vulnDetail));

      const updatedVulnDetail = isDetailVulnSelected ? { ...vulnDetail, ...data } : { ...vulnDetail };

      if (selectAll) {
        await api.manage.updateAllVulns(workspaceSelected, { filters: queryParam.filters }, data);

        const updatedVulnsList = vulnsList.map((vuln) => ({ ...vuln, ...data }));
        const updatedSelectedVulns = vulnsSelected.map((vuln) => ({ ...vuln, ...data }));

        dispatch(refreshVulnsList(updatedVulnsList, updatedSelectedVulns, updatedVulnDetail));
      } else {
        const payload = { ids: vulnIDs, ...data };

        await api.manage.updateVulns(workspaceSelected, payload);

        const updatedVulnsList = vulnsList.map((vuln) => {
          const isSelected = vulnsSelected.some((selectedVuln) => sameVulnId(selectedVuln, vuln));
          if (isSelected) return { ...vuln, ...data };
          return vuln;
        });

        const updatedSelectedVulns = vulnsSelected.map((vuln) => ({ ...vuln, ...data }));

        dispatch({ type: types.MANAGE_SET_BULK_UPDATE_SUCCESS });
        dispatch(refreshVulnsList(updatedVulnsList, updatedSelectedVulns, updatedVulnDetail));
      }

      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
      dispatch({ type: types.MANAGE_BULK_UPDATE_FINISHED });
    } catch (e) {
      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
      if (field === 'name') dispatch({ type: types.SET_ERROR_MESSAGE, errorMessage: e.message });
      else {
        dispatch({ type: types.MANAGE_VULN_ERROR, errorMessage: e.message || 'An error occured while updating vulns' });
        dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
      }
    }
  };
}

export function saveTemplateFromVuln () {
  return async (dispatch, getState) => {
    const vulnsSelected = selectVulnsSelected(getState());
    const workspaceSelected = selectCurrentWorkspace(getState());

    const getVulnsData = vulnsSelected.map((vuln) => api.manage.fetchById(workspaceSelected, getVulnId(vuln)));

    const vulnsData = await Promise.all(getVulnsData);

    const save = async (template) => {
      const response = await api.knowledgeBase.createTemplate(template);
      return response;
    };

    const data = vulnsData.map((vuln) => {
      const filteredReferences = vuln.refs.filter(ref => ref.name).map(filteredRef => filteredRef.name);
      const template = {
        cwe: '',
        cve: vuln.cve,
        description: vuln.desc,
        desc: vuln.desc,
        data: vuln.data,
        exploitation: vuln.severity,
        name: vuln.name,
        references: filteredReferences,
        refs: filteredReferences,
        resolution: vuln.resolution,
        impact: vuln.impact,
        policyviolations: vuln.policyviolations,
        customfields: vuln.custom_fields,
        easeofresolution: vuln.easeofresolution,
        external_id: vuln.external_id,
        type: 'vulnerability_template'
      };
      return save(template);
    });

    Promise.allSettled(data).then((results) => {
      const templatesCreated = results ? results.filter((template) => template.status === 'fulfilled').map((t) => t.value) : [];
      const errors = results ? results.filter((template) => template.status === 'rejected').map((t) => t.reason.object) : [];
      dispatch({ type: types.SAVE_TEMPLATE_MANAGE, templatesCreated, errors });
    });
  };
}

export function getTemplatesMassiveUpdate () {
  return async (dispatch) => {
    dispatch({ type: types.GET_TEMPLATES_BULK_START });

    try {
      const data = await api.manage.getTemplates();
      dispatch({ type: types.GET_TEMPLATES_BULK_SUCCESS, data });
    } catch (e) {
      dispatch({ type: types.GET_TEMPLATES_BULK_FAIL });
    }
  };
}

export function showBulkUpdateModal (bulkUpdateField, bulkUpdateValue) {
  return (dispatch) => {
    dispatch(setBulkUpdateField(bulkUpdateField));
    dispatch(setBulkUpdateValue(bulkUpdateValue));
    dispatch(openModal(MODAL_MANAGE_BULK_UPDATE));
  };
}

export function onGetVulnsFail (errorMessage) {
  return (dispatch) => {
    dispatch({ type: types.SEARCH_VULNS_FAIL, data: errorMessage });
  };
}

// Summary: Delete vuln/s database
export function deleteSelectedVulns () {
  return async (dispatch, getState) => {
    const workspaceSelected = selectCurrentWorkspace(getState());
    const vulnsSelected = selectVulnsSelected(getState());
    const vulnIDs = vulnsSelected.map(getVulnId).filter((id) => id !== null && typeof id !== 'undefined');
    const groupByField = selectGroupByField('vulns', getState());
    const vulnsList = selectVulnsList(getState());
    const selectAll = selectSelectAll(getState());

    const advancedFilterQueryParam = selectAdvancedFilterQueryParam(getState(), 'vulns');
    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectQueryParam('vulns', getState());
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    dispatch({ type: types.HIDE_MODAL_DELETE_CONFIRMATION_VULNS_SUCCEED });
    dispatch({ type: types.DELETE_VULNS });

    try {
      if (selectAll) {
        await api.manage.deleteAllVulns(workspaceSelected, { filters: queryParam.filters });
      } else {
        if (vulnIDs.length === 1) await api.manage.deleteVuln(workspaceSelected, vulnIDs[0]);
        else await api.manage.deleteVulns(workspaceSelected, vulnIDs);
      }
      dispatch(getData());
      if (groupByField) {
        const newVulnsList = vulnsList.map((group) => {
          const groupedData = get(group, 'groupData', null);
          if (groupedData) {
            const newGroupedData = groupedData.filter((vuln) => !vulnIDs.some((id) => sameVulnId(vuln, { id })));
            return {
              ...group,
              count: newGroupedData.length,
              groupData: newGroupedData
            };
          }
          return group;
        });
        dispatch({ type: types.UPDATE_VULNS_LIST, vulnsList: newVulnsList });
      } else dispatch(newGetVulns());
      dispatch(getVulnsCountInWs());
    } catch (e) {
      dispatch({ type: types.MANAGE_VULN_ERROR, errorMessage: e.message || 'An error occured while deleting vulns' });
    }
  };
}

export function removeMockVuln () {
  return (dispatch, getState) => {
    const vulnList = selectVulnsList(getState());
    const newVulnsList = vulnList.filter((vuln) => vuln._id !== MOCK_VULN_ID);
    const vulnsCount = newVulnsList.length > 0 ? newVulnsList.length - 1 : 0;
    dispatch({ type: types.REMOVE_MOCK_VULN, vulnsList: newVulnsList, vulnsCount });
  };
}

export function endTour () {
  return async (dispatch, getState) => {
    const usesMockVulns = selectUsesMockVulns(getState());
    if (usesMockVulns) dispatch(removeMockVuln());
    dispatch(setTourFinished(true));
  };
}

export function downloadVulns (exportAllData) {
  return async (dispatch, getState) => {
    const workspace = selectCurrentWorkspace(getState());
    const fields = selectManageFields(getState());
    const columnsIncluded = getExportCsvColumns(fields, VALID_COLUMNS);
    const state = getState();
    let advancedFilterQueryParam = [];
    try {
      if (exportAllData) advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulns', true);
      else advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulns', true, columnsIncluded);
    } catch (e) {
      dispatch(setFilterError('vulns', 'Syntax error. Please try again. For further help check our documentation'));
      return dispatch(setVulnsFilterError());
    }

    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    let standardQueryParam = [];
    if (exportAllData) standardQueryParam = selectQueryParam('vulns', state, true);
    else standardQueryParam = selectQueryParam('vulns', state, true, columnsIncluded);
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    try {
      dispatch({ type: types.EXPORT_CSV_MANAGE_REQUEST });
      let response = '';
      if (exportAllData) response = await api.manage.exportCSVAllVulns(workspace, queryParam);
      else response = await api.manage.exportCSVFiltered(workspace, queryParam);
      dispatch(exportCsv(response));
    } catch (err) {
      dispatch({ type: types.EXPORT_CSV_MANAGE_FAILURE });
    }

    return dispatch({ type: types.EXPORT_CSV_MANAGE_SUCCESS });
  };
}

export function showContextMenu (show, XPos, YPos) {
  return (dispatch, getState) => {
    const state = getState();
    const currentXPos = selectContextMenuXPos(state);
    const currentYPos = selectContextMenuYPos(state);
    const newXPos = XPos ? (XPos + 1) : currentXPos;
    const newYPos = YPos ? (YPos + 1) : currentYPos;

    dispatch({
      type: types.SHOW_CONTEXT_MENU, show, contextMenuXPos: newXPos, contextMenuYPos: newYPos
    });
  };
}

export function closeTemplateModalMessage () {
  return (dispatch) => {
    dispatch({ type: types.CLOSE_TEMPLATE_MODAL_MESSAGE });
  };
}

export function setError (errorMessage) {
  return (dispatch) => {
    dispatch({ type: types.SET_ERROR, data: errorMessage });
  };
}

export const getVulnRefs = (id) => async (dispatch, getState) => {
  const workspaceSelected = selectCurrentWorkspace(getState());
  try {
    const response = await api.manage.fetchById(workspaceSelected, id);
    const refs = get(response, 'refs', []);
    dispatch(showBulkUpdateModal('references', refs.map((r) => r.name)));
  } catch (e) {
    dispatch({ type: types.MANAGE_VULN_ERROR, errorMessage: e.message || 'An error has occurred' });
  }
};

export function endNewTour (endFullTour, flag) {
  return async (dispatch) => {
    if (endFullTour) dispatch(setNewTourFinished({ sidebarTourFinished: true, dashboardTourFinished: true, assetVulnsTourFinished: true, vulnsTourFinished: true }));
    else dispatch(setNewTourFinished({ [flag]: true }));
  };
}

export function getVulnsTotalCount () {
  return async (dispatch, getState) => {
    const selectedWs = selectCurrentWorkspace(getState());
    const response = await api.workspace.getWsStats(selectedWs);
    const count = get(response, 'stats.total_vulns', 0);
    dispatch({ type: types.GET_VULNS_TOTAL_COUNT, count });
  };
}

export function saveTemplateFromVulnContext (vulnsSelected, entity) {
  return async (dispatch, getState) => {
    const isOutsideWorkspace = entity === 'vulnsGeneral';
    const workspaceSelected = selectCurrentWorkspace(getState());

    const getVulnsData = vulnsSelected.map((vuln) => {
      if (isOutsideWorkspace) return api.vulnsGeneral.fetchVulnById(getVulnId(vuln));
      else return api.manage.fetchById(workspaceSelected, getVulnId(vuln));
    });

    const vulnsData = await Promise.all(getVulnsData);

    const save = async (template) => {
      const response = await api.knowledgeBase.createTemplate(template);
      return response;
    };

    const data = vulnsData.map((vuln) => {
      const filteredReferences = vuln.refs.filter(ref => ref.name).map(filteredRef => filteredRef.name);
      const template = {
        cwe: '',
        cve: vuln.cve,
        description: vuln.desc,
        desc: vuln.desc,
        data: vuln.data,
        exploitation: vuln.severity,
        name: vuln.name,
        references: filteredReferences,
        refs: filteredReferences,
        resolution: vuln.resolution,
        impact: vuln.impact,
        policyviolations: vuln.policyviolations,
        customfields: vuln.custom_fields,
        easeofresolution: vuln.easeofresolution,
        external_id: vuln.external_id,
        type: 'vulnerability_template'
      };
      return save(template);
    });

    Promise.allSettled(data).then((results) => {
      const templatesCreated = results ? results.filter((template) => template.status === 'fulfilled').map((t) => t.value) : [];
      const errors = results ? results.filter((template) => template.status === 'rejected').map((t) => t.reason.object) : [];
      dispatch({ type: types.SAVE_TEMPLATE_MANAGE, templatesCreated, errors });
    });
  };
}

export function downloadVulnsContext (entity, workspace) {
  return async (dispatch, getState) => {
    const state = getState();
    let advancedFilterQueryParam = [];
    try {
      advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, entity, true);
    } catch (e) {
      dispatch(setFilterError(entity, 'Syntax error. Please try again. For further help check our documentation'));
      return dispatch(setVulnsFilterError());
    }

    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectQueryParam(entity, state, true);
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    try {
      dispatch({ type: types.EXPORT_CSV_MANAGE_REQUEST });
      const response = await api.manage.exportCSV(workspace, queryParam);
      dispatch(exportCsv(response));
    } catch (err) {
      dispatch({ type: types.EXPORT_CSV_MANAGE_FAILURE });
      if (entity === 'vulnsAssets') dispatch({ type: VULN_ASSET_ERROR, errorMessage: 'There was an error, please try again.' });
    }

    return dispatch({ type: types.EXPORT_CSV_MANAGE_SUCCESS });
  };
}
